import liveServerStatus from '../../liveServerStatus';

/**
 * Generate HTML email template for claim request notifications
 * @param {Object} claimData - The claim request data
 * @param {Object} userData - User data of the requester
 * @param {string} recipientName - Name of the email recipient
 * @param {string} status - Status of the claim request (pending, approved, rejected)
 * @returns {string} - HTML email content
 */
export const generateClaimRequestEmailHTML = (
  claimData,
  userData,
  recipientName,
  status = 'pending'
) => {
  // Set up variables based on status
  let subject, message, actionText, headerColor;
  const isManager = recipientName !== userData.firstName;
  
  // Configure email content based on status and recipient
  switch (status) {
    case 'approved':
      headerColor = '#4caf50'; // green
      subject = 'Claim Request Approved';
      message = isManager 
        ? `You have approved the claim request from ${userData.firstName} ${userData.lastName}.`
        : 'Your claim request has been approved.';
      actionText = 'View Details';
      break;
    case 'rejected':
      headerColor = '#f44336'; // red
      subject = 'Claim Request Rejected';
      message = isManager 
        ? `You have rejected the claim request from ${userData.firstName} ${userData.lastName}.`
        : 'Your claim request has been rejected.';
      actionText = 'View Details';
      break;
    default: // pending
      headerColor = '#ff9800'; // orange
      subject = 'New Claim Request';
      message = isManager 
        ? `${userData.firstName} ${userData.lastName} has submitted a new claim request for your approval.`
        : 'Your claim request has been submitted successfully and is awaiting approval.';
      actionText = isManager ? 'Review Request' : 'View Request';
  }

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: claimData.currency || 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  // Add test environment indicator if not in live environment
  const testEnvironmentBanner = !liveServerStatus ? `
    <div style="background-color: #ffeb3b; color: #333; text-align: center; padding: 5px; font-weight: bold; font-size: 12px;">
      ⚠️ TEST EMAIL - No action required ⚠️
    </div>
  ` : '';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${subject}</title>
      <style>
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          line-height: 1.6;
          color: #333;
          margin: 0;
          padding: 0;
          background-color: #f5f5f5;
        }
        .email-container {
          max-width: 600px;
          margin: 0 auto;
          background-color: #ffffff;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
        }
        .email-header {
          background-color: ${headerColor};
          color: white;
          padding: 20px;
          text-align: center;
        }
        .email-header h1 {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
        }
        .email-body {
          padding: 30px;
        }
        .greeting {
          font-size: 18px;
          font-weight: 500;
          margin-bottom: 20px;
          color: #212121;
        }
        .message {
          margin-bottom: 25px;
          color: #424242;
        }
        .claim-details {
          background-color: #f9f9f9;
          border-left: 4px solid ${headerColor};
          padding: 15px;
          margin-bottom: 20px;
          border-radius: 4px;
        }
        .claim-details h3 {
          margin-top: 0;
          margin-bottom: 15px;
          color: #424242;
        }
        .claim-detail-item {
          margin-bottom: 10px;
        }
        .claim-detail-label {
          font-weight: 500;
          color: #616161;
        }
        .divider {
          height: 1px;
          background-color: #e0e0e0;
          margin: 25px 0;
        }
        .cta-button {
          display: inline-block;
          background-color: ${headerColor};
          color: white;
          text-decoration: none;
          padding: 12px 25px;
          border-radius: 4px;
          font-weight: 500;
          margin-top: 15px;
          text-align: center;
        }
        .email-footer {
          background-color: #212121;
          padding: 20px;
          text-align: center;
          color: #f5f5f5;
          font-size: 12px;
        }
        .company-name {
          font-weight: 600;
          color: #f5f5f5;
          margin-bottom: 8px;
        }
        .amount {
          font-weight: bold;
          color: ${headerColor};
        }
      </style>
    </head>
    <body>
      ${testEnvironmentBanner}
      <div class="email-container">
        <div class="email-header">
          <h1>${subject}</h1>
        </div>
        
        <div class="email-body">
          <div class="greeting">
            Hello ${recipientName || 'there'},
          </div>
          
          <div class="message">
            ${message}
          </div>
          
          <div class="claim-details">
            <h3>Claim Request Details</h3>
            <div class="claim-detail-item">
              <span class="claim-detail-label">Employee:</span> ${userData.firstName} ${userData.lastName}
            </div>
            <div class="claim-detail-item">
              <span class="claim-detail-label">Type:</span> ${claimData.type}
            </div>
            <div class="claim-detail-item">
              <span class="claim-detail-label">Date:</span> ${formatDate(claimData.date)}
            </div>
            <div class="claim-detail-item">
              <span class="claim-detail-label">Amount:</span> <span class="amount">${formatCurrency(claimData.amount)}</span>
            </div>
            ${claimData.description ? `
            <div class="claim-detail-item">
              <span class="claim-detail-label">Description:</span> ${claimData.description}
            </div>
            ` : ''}
            <div class="claim-detail-item">
              <span class="claim-detail-label">Status:</span> <strong style="color: ${headerColor};">${status.charAt(0).toUpperCase() + status.slice(1)}</strong>
            </div>
          </div>
          
          <a href="/claims" class="cta-button">
            ${actionText}
          </a>
          
          <div class="divider"></div>
          
          <div class="message">
            If you need any assistance, please contact the HR team.
          </div>
        </div>
        
        <div class="email-footer">
          <div class="company-name">MyelinZ PPC Portal</div>
          <p>This is an automated message. Please do not reply directly to this email.</p>
          <p>© ${new Date().getFullYear()} MyelinZ. All rights reserved.</p>
          ${!liveServerStatus ? '<p><strong>TEST ENVIRONMENT - No action required</strong></p>' : ''}
        </div>
      </div>
    </body>
    </html>
  `;
};

export default generateClaimRequestEmailHTML; 